import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import L from 'leaflet';
import { Send } from 'lucide-react';
import { StatusBadge, StatusPill, formatDate, formatDateTime, formatNumber } from '@/components/live';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { SATELLITE_ATTRIBUTION, SATELLITE_URL } from '@/screens/parts/PlotMap';

interface BoundaryRow {
  geometry_id: string; version_no: number | null; is_current: boolean; approval_status: string; area_ha: number | null;
  capture_method: string | null; created_at: string; created_by_name: string | null; approved_at: string | null; open_request_id: string | null;
}

const STATE_WORDS: Record<string, string> = {
  draft: 'Draft - not yet submitted', submitted: 'Waiting for approval', approved: 'Approved', rejected: 'Rejected - redraw it',
};

/**
 * The plot's boundary and where it stands (owner, 2026-09-28: "build the boundary approval step").
 * A redrawn boundary is a draft; the satellite keeps watching the last approved one until this one
 * is submitted and approved by someone other than whoever drew it.
 */
export function BoundaryStatus({ plotId, plotCode }: { plotId: string; plotCode: string }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const maySubmit = can(profile, 'plot.mapping', 'submit');
  const [busy, setBusy] = useState(false);
  const boundaries = useQuery({
    queryKey: ['plot-boundaries', plotId],
    enabled: isSupabaseConfigured && Boolean(plotId),
    queryFn: () => callRpc<BoundaryRow[]>('fn_plot_boundaries', { p_plot_id: plotId }),
  });
  const rows = boundaries.data ?? [];
  const current = rows.find((row) => row.is_current);
  // Numbered by order drawn (the stored version_no is not kept per boundary): the oldest is v1.
  const version = current ? rows.length - rows.indexOf(current) : 0;
  const watched = rows.find((row) => row.approval_status === 'approved');
  if (!current) return null;

  async function submit() {
    if (!current) return;
    setBusy(true);
    try {
      await callRpc('fn_submit_for_approval', {
        p_entity_table: 'geo.plot_geometries', p_entity_id: current.geometry_id, p_workflow_code: 'BOUNDARY_APPROVE',
        p_entity_label: `Boundary of ${plotCode} (${formatNumber(current.area_ha, 2)} ha)`,
      });
      await queryClient.invalidateQueries({ queryKey: ['plot-boundaries', plotId] });
      await queryClient.invalidateQueries({ queryKey: ['approval_inbox'] });
      toast.success('Boundary submitted. It is approved in Approvals, by another administrator - or by you, with your PIN, if you are the only one.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The boundary could not be submitted.');
    } finally {
      setBusy(false);
    }
  }

  const pending = current.approval_status !== 'approved';
  return (
    <div className={`boundary-status${pending ? ' is-pending' : ''}`}>
      <span className="cell-stack">
        <span><strong>Boundary v{version}</strong> · {formatNumber(current.area_ha, 3)} ha ·{' '}
          <StatusBadge label={STATE_WORDS[current.approval_status] ?? current.approval_status}
                       tone={current.approval_status === 'approved' ? 'green' : current.approval_status === 'rejected' ? 'red' : 'amber'} /></span>
        <small className="muted">
          Drawn {formatDateTime(current.created_at)}{current.created_by_name ? ` by ${current.created_by_name}` : ''}
          {current.approval_status === 'approved' && current.approved_at ? ` · approved ${formatDate(current.approved_at)}` : ''}
          {pending && (watched && watched.geometry_id !== current.geometry_id
            ? ` · the satellite still watches the approved boundary (${formatNumber(watched.area_ha, 3)} ha) until this one is approved`
            : ' · the satellite starts watching it once it is approved')}
        </small>
      </span>
      {pending && ['draft', 'rejected'].includes(current.approval_status) && maySubmit && (
        <button className="primary" disabled={busy} onClick={() => void submit()} type="button">
          <Send size={14} /> {busy ? 'Submitting...' : 'Submit boundary for approval'}
        </button>
      )}
    </div>
  );
}

interface Review {
  plot_code: string; declared_area_ha: number | null; geometry: GeoJSON.Geometry; area_ha: number | null; status: string;
  captured: string | null; drawn_at: string;
  previous: { geometry: GeoJSON.Geometry; area_ha: number | null; approved_at: string | null } | null;
  moved_m: number | null; overlap_pct: number | null;
}

/**
 * What an approver sees for a boundary: the new shape (yellow) over satellite imagery, the approved
 * one it would replace (blue, dashed), both areas, how far the centre moved and how much they overlap.
 */
export function BoundaryReview({ geometryId }: { geometryId: string }) {
  const review = useQuery({
    queryKey: ['boundary-review', geometryId],
    enabled: isSupabaseConfigured,
    queryFn: () => callRpc<Review | null>('fn_boundary_review', { p_geometry_id: geometryId }),
  });
  const container = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const data = review.data;
    if (!container.current || !data?.geometry) return undefined;
    const map = L.map(container.current, { zoomControl: true, attributionControl: true });
    L.tileLayer(SATELLITE_URL, { attribution: SATELLITE_ATTRIBUTION, maxNativeZoom: 18, maxZoom: 20 }).addTo(map);
    const layers: L.GeoJSON[] = [];
    if (data.previous?.geometry) {
      layers.push(L.geoJSON(data.previous.geometry as GeoJSON.GeoJsonObject, { style: { color: '#60a5fa', weight: 3, dashArray: '6,6', fillOpacity: 0.05 } }).addTo(map));
    }
    layers.push(L.geoJSON(data.geometry as GeoJSON.GeoJsonObject, { style: { color: '#facc15', weight: 3, fillColor: '#facc15', fillOpacity: 0.12 } }).addTo(map));
    const bounds = layers[0]!.getBounds();
    for (const layer of layers.slice(1)) bounds.extend(layer.getBounds());
    map.fitBounds(bounds.pad(0.4), { maxZoom: 18 });
    setTimeout(() => map.invalidateSize(), 150);
    return () => { map.remove(); };
  }, [review.data]);

  const data = review.data;
  if (review.isLoading) return <p className="state-note">Loading the boundary...</p>;
  if (!data) return <p className="muted">This boundary could not be read.</p>;
  const far = data.moved_m != null && data.moved_m > 1000;
  return (
    <section className="card" style={{ display: 'grid', gap: 8 }}>
      <h3 style={{ margin: 0 }}>Boundary of {data.plot_code}</h3>
      <div className="boundary-review-map" ref={container} />
      <small className="muted">Yellow: the boundary to approve. {data.previous ? 'Blue, dashed: the approved boundary it replaces.' : 'The plot has no approved boundary yet.'}</small>
      <dl className="detail-grid">
        <div><dt>New area</dt><dd>{formatNumber(data.area_ha, 3)} ha{data.declared_area_ha ? ` (declared ${formatNumber(data.declared_area_ha, 3)} ha)` : ''}</dd></div>
        {data.previous && <div><dt>Approved area now</dt><dd>{formatNumber(data.previous.area_ha, 3)} ha</dd></div>}
        {data.moved_m != null && (
          <div><dt>Centre moved</dt><dd>{data.moved_m >= 1000 ? `${formatNumber(data.moved_m / 1000, 1)} km` : `${formatNumber(data.moved_m)} m`}
            {far && <> <StatusPill value="check" /></>}</dd></div>
        )}
        {data.overlap_pct != null && <div><dt>Overlap with the approved boundary</dt><dd>{formatNumber(data.overlap_pct, 1)}%</dd></div>}
        <div><dt>Captured</dt><dd>{data.captured ?? '--'} · {formatDateTime(data.drawn_at)}</dd></div>
      </dl>
      {far && (
        <p className="login-alert" role="alert" style={{ margin: 0 }}>
          The new boundary is {formatNumber((data.moved_m ?? 0) / 1000, 1)} km from the approved one: not a correction of the same land. Check the land
          records before approving; on approval the satellite watches the new place and closes the old one's monitoring.
        </p>
      )}
    </section>
  );
}
